import { Button, Modal } from '@orobix/orobix-fe-components'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import CounterList from './CountersSettings/CountersList'
import LanguageSelect from './LanguageSettings/LanguageSelect'
import NotificationsSettings from './NotificationsSettings/NotificationsSettings'
import useStyles from './styles'
import { Settings } from './types'
import UserlevelSelect from './UserLevelSettings/UserlevelSelect'
import useSettings from './useSettings'
import useUpdateSettings from './useUpdateSettings'

export interface SettingsModalProps {
  onCloseRequest: () => void
}

const SettingsModal = ({ onCloseRequest }: SettingsModalProps) => {
  const { t } = useTranslation()
  const { i18n } = useTranslation()
  const classes = useStyles()

  const { settings, isFetching } = useSettings()
  const [updatedSettings, setUpdatedSettings] = useState<Partial<Settings>>({})
  const { updateSettings, inProgress } = useUpdateSettings(onCloseRequest)

  const mergeSettings = (newSettings: Partial<Settings>) => {
    setUpdatedSettings((prevSettings) => ({
      ...prevSettings,
      ...newSettings,
    }))
  }

  const applySettings = (newSettings: Partial<Settings>) => {
    if (newSettings?.preference__user_language) {
      i18n.changeLanguage(newSettings.preference__user_language)
    }
  }

  return (
    <Modal
      size="xl"
      onCloseRequest={onCloseRequest}
      title={t('Settings')}
      footer={
        <>
          <Button
            onClick={() => {
              onCloseRequest()
            }}
            label={t('Close')}
            type="default"
            buttonType="button"
          />
          <Button
            onClick={() => {
              applySettings(updatedSettings)
              updateSettings(updatedSettings)
            }}
            label={t('Save')}
            type="action"
            buttonType="button"
            loading={inProgress}
            disabled={Object.keys(updatedSettings).length === 0}
          />
        </>
      }
    >
      <section className={classes.section}>
        <LanguageSelect
          settings={settings}
          updateSettings={mergeSettings}
          isLoading={isFetching || inProgress}
        />
      </section>
      <section className={classes.section}>
        <UserlevelSelect
          settings={settings}
          updateSettings={mergeSettings}
          isLoading={isFetching || inProgress}
        />
      </section>
      <section className={classes.section}>
        <NotificationsSettings
          settings={settings}
          updateSettings={mergeSettings}
          isLoading={isFetching || inProgress}
        />
      </section>
      <section className={classes.section}>
        <CounterList />
      </section>
    </Modal>
  )
}

export default SettingsModal
